import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import HoverTooltip from '../../../components/ui/HoverTooltip';

const BiddingPanel = ({
  selectedProduct,
  onBidSubmit,
  isOpen,
  isMobile = false,
  canBid = true,
  isOwnProduct = false,
  ownProductBidTooltip = '',
  ownProductContactTooltip = '',
  onRequireMarketUser,
  onContactSeller,
}) => {
  const [bidAmount, setBidAmount] = useState('');
  const [bidQuantity, setBidQuantity] = useState('');
  const [bidMessage, setBidMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!selectedProduct) {
      return;
    }

    setBidAmount('');
    setBidQuantity('');
    setBidMessage('');
  }, [selectedProduct?.id]);

  const handleSubmitBid = async (event) => {
    event.preventDefault();
    if (!selectedProduct || !bidAmount || !bidQuantity) {
      return;
    }

    const isOfferOpen = selectedProduct?.openToOffer !== false;

    if (!canBid) {
      onRequireMarketUser?.({
        loginMessage: 'Teklif göndermek için pazaryeri hesabı ile giriş yapmalısınız.',
        nonMarketUserMessage: 'Teklif gönderme sadece pazaryeri kullanıcıları tarafından yapılabilir.',
      });
      return;
    }

    if (isOwnProduct || !isOfferOpen) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onBidSubmit({
        productId: selectedProduct.id,
        amount: parseFloat(bidAmount),
        quantity: parseInt(bidQuantity, 10),
        message: bidMessage,
      });

      setBidAmount('');
      setBidQuantity('');
      setBidMessage('');
    } catch (error) {
      console.error('Teklif gönderilirken hata oluştu:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrentHighestBid = () => {
    if (!selectedProduct?.currentBids?.length) {
      return null;
    }

    return Math.max(...selectedProduct.currentBids.map((bid) => bid.amount));
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatTimestamp = (timestamp) =>
    new Date(timestamp).toLocaleString('tr-TR', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

  if (!selectedProduct) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center">
          <Icon name="Package" size={48} className="text-secondary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">Bir Ürün Seçin</h3>
          <p className="text-text-secondary text-sm">
            Teklif göndermek ve detayları görmek için listeden ürün seçin
          </p>
        </div>
      </div>
    );
  }

  const highestBid = getCurrentHighestBid();
  const suggestedBid = selectedProduct.price * 0.9;
  const isOfferOpen = selectedProduct?.openToOffer !== false;
  const availableQuantity = Number(
    selectedProduct?.availableQuantity ?? selectedProduct?.quantity ?? 0,
  );
  const isBidBlocked = !canBid || !isOfferOpen || isOwnProduct;

  const handleContactAction = (channel) => {
    if (!selectedProduct || isOwnProduct) {
      return;
    }

    if (!canBid) {
      onRequireMarketUser?.({
        loginMessage: 'Satıcıya ulaşmak için pazaryeri hesabı ile giriş yapmalısınız.',
        nonMarketUserMessage: 'Satıcıya ulaşma sadece pazaryeri kullanıcıları tarafından yapılabilir.',
        postLoginAction: {
          type: 'open-contact',
          productId: selectedProduct.id,
        },
      });
      return;
    }

    if (typeof onContactSeller === 'function') {
      onContactSeller(selectedProduct, { channel });
      return;
    }

    if (!selectedProduct?.seller?.phone) {
      return;
    }

    const protocol = channel === 'TEXT' ? 'sms' : 'tel';
    window.open(`${protocol}:${selectedProduct.seller.phone}`, '_self');
  };

  return (
    <div className={`h-full flex flex-col ${isMobile ? '' : 'bg-surface'}`}>
      <div className="p-4 border-b border-border">
        <div className="flex items-start space-x-3">
          <Image
            src={selectedProduct.images?.[0]}
            alt={selectedProduct.title}
            className="w-16 h-16 rounded-md object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-text-primary truncate">{selectedProduct.title}</h3>
            <p className="text-sm text-text-secondary truncate">{selectedProduct.seller?.name}</p>
            <p className="text-lg font-bold text-primary">
              {formatCurrency(selectedProduct.price)} {selectedProduct.priceUnit}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 border-b border-border">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-text-secondary">Kalan:</span>
            <p className="font-medium">
              {availableQuantity} {selectedProduct.unit}
            </p>
          </div>
          <div>
            <span className="text-text-secondary">Kategori:</span>
            <p className="font-medium capitalize">{selectedProduct.category}</p>
          </div>
          <div>
            <span className="text-text-secondary">Yayinda:</span>
            <p className="font-medium">{selectedProduct.daysListed} gün önce</p>
          </div>
          <div>
            <span className="text-text-secondary">Satıcı Puanı:</span>
            <div className="flex items-center space-x-1">
              <span className="font-medium">{selectedProduct.seller?.rating}</span>
              <Icon name="Star" size={12} className="text-warning fill-current" />
            </div>
          </div>
        </div>
      </div>

      {selectedProduct.currentBids?.length > 0 && (
        <div className="p-4 border-b border-border">
          <h4 className="font-semibold text-text-primary mb-3">Mevcut Teklifler</h4>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {selectedProduct.currentBids
              .sort((left, right) => right.amount - left.amount)
              .slice(0, 3)
              .map((bid) => (
                <div
                  key={bid.id}
                  className="flex items-center justify-between p-2 bg-background rounded-md"
                >
                  <div>
                    <p className="font-medium text-sm">{formatCurrency(bid.amount)}</p>
                    <p className="text-xs text-text-secondary">
                      {bid.acceptedQuantity || bid.quantity} {selectedProduct.unit} •{' '}
                      {formatTimestamp(bid.timestamp)}
                    </p>
                  </div>
                  <div className="text-right text-xs text-text-secondary">
                    <div>{bid.bidder}</div>
                    {bid.status === 'ACCEPTED' ? (
                      <div className="mt-1 text-success">Kabul edildi</div>
                    ) : null}
                  </div>
                </div>
              ))}
          </div>
          {highestBid ? (
            <div className="mt-2 p-2 bg-success-100 rounded-md">
              <p className="text-sm font-medium text-success-800">
                En Yüksek Teklif: {formatCurrency(highestBid)}
              </p>
            </div>
          ) : null}
        </div>
      )}

      <div className="flex-1 p-4">
        <h4 className="font-semibold text-text-primary mb-4">Teklifini Gönder</h4>

        {!canBid ? (
          <div className="mb-4 rounded-md border border-warning bg-warning-100 px-3 py-2 text-sm text-warning">
            Teklif göndermek için pazaryeri hesabı ile giriş yapmalısınız.
          </div>
        ) : null}

        {isOwnProduct ? (
          <div className="mb-4 rounded-md border border-warning bg-warning-100 px-3 py-2 text-sm text-warning">
            Bu ürün size ait. Teklif gönderemez veya iletişim başlatamazsınız.
          </div>
        ) : null}

        {!isOfferOpen ? (
          <div className="mb-4 rounded-md border border-secondary-300 bg-secondary-100 px-3 py-2 text-sm text-text-secondary">
            Bu ürün şu anda teklife açık değil.
          </div>
        ) : null}

        <form onSubmit={handleSubmitBid} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Teklif Tutarı (/{selectedProduct.priceUnit?.split('per ')[1] || 'birim'})
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-text-secondary">$</span>
              </div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={bidAmount}
                onChange={(event) => setBidAmount(event.target.value)}
                placeholder={suggestedBid.toFixed(2)}
                disabled={isBidBlocked}
                className="block w-full pl-7 pr-3 py-2 border border-border rounded-md text-sm focus:border-border-focus focus:ring-1 focus:ring-primary-500 transition-all duration-200 ease-out disabled:opacity-60 disabled:cursor-not-allowed"
                required
              />
            </div>
            {!highestBid ? (
              <p className="text-xs text-text-secondary mt-1">
                Önerilen: {formatCurrency(suggestedBid)}
              </p>
            ) : null}
            {highestBid && parseFloat(bidAmount) <= highestBid && bidAmount ? (
              <p className="text-xs text-warning mt-1">
                Teklifin {formatCurrency(highestBid)} tutarından yüksek olmalı
              </p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Miktar ({selectedProduct.unit})
            </label>
            <input
              type="number"
              min="1"
              max={availableQuantity}
              value={bidQuantity}
              onChange={(event) => setBidQuantity(event.target.value)}
              placeholder={`Maks: ${availableQuantity}`}
              disabled={isBidBlocked}
              className="block w-full px-3 py-2 border border-border rounded-md text-sm focus:border-border-focus focus:ring-1 focus:ring-primary-500 transition-all duration-200 ease-out disabled:opacity-60 disabled:cursor-not-allowed"
              required
            />
            <p className="text-xs text-text-secondary mt-1">
              Kullanılabilir miktar: {availableQuantity} {selectedProduct.unit}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Satıcıya Mesaj (Opsiyonel)
            </label>
            <textarea
              value={bidMessage}
              onChange={(event) => setBidMessage(event.target.value)}
              placeholder="Özel talep veya sorularınızı yazın..."
              rows={3}
              disabled={isBidBlocked}
              className="block w-full px-3 py-2 border border-border rounded-md text-sm focus:border-border-focus focus:ring-1 focus:ring-primary-500 transition-all duration-200 ease-out resize-none disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>

          {bidAmount && bidQuantity ? (
            <div className="p-3 bg-primary-50 rounded-md">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Toplam Teklif:</span>
                <span className="text-lg font-bold text-primary">
                  {formatCurrency(parseFloat(bidAmount) * parseInt(bidQuantity || 1, 10))}
                </span>
              </div>
              <p className="text-xs text-text-secondary mt-1">
                {bidQuantity} {selectedProduct.unit} x {formatCurrency(parseFloat(bidAmount))}
              </p>
            </div>
          ) : null}

          <HoverTooltip
            content={isOwnProduct ? ownProductBidTooltip : undefined}
            className="block w-full"
          >
            <button
              type="submit"
              disabled={
                isBidBlocked ||
                !bidAmount ||
                !bidQuantity ||
                isSubmitting ||
                (highestBid && parseFloat(bidAmount) <= highestBid)
              }
              className="w-full bg-primary text-white py-3 px-4 rounded-md font-semibold hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 ease-out disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Teklif Gönderiliyor...</span>
                </div>
              ) : isOwnProduct ? (
                'Kendi Ürünün'
              ) : !isOfferOpen ? (
                'Teklife Kapalı'
              ) : canBid ? (
                'Teklif Gönder'
              ) : (
                'Giriş Gerekli'
              )}
            </button>
          </HoverTooltip>
        </form>
      </div>

      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Image
              src={selectedProduct.seller?.avatar}
              alt={selectedProduct.seller?.name}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <p className="text-sm font-medium text-text-primary">{selectedProduct.seller?.name}</p>
              <p className="text-xs text-text-secondary">{selectedProduct.seller?.phone}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <HoverTooltip
              content={isOwnProduct ? ownProductContactTooltip : undefined}
              className="inline-flex"
            >
              <button
                onClick={() => handleContactAction('PHONE')}
                disabled={isOwnProduct}
                className={`p-2 rounded-md transition-all duration-200 ease-out ${
                  isOwnProduct
                    ? 'bg-secondary-200 text-text-secondary cursor-not-allowed'
                    : 'bg-accent-100 text-accent-700 hover:bg-accent-500 hover:text-white'
                }`}
              >
                <Icon name="Phone" size={16} />
              </button>
            </HoverTooltip>
            <HoverTooltip
              content={isOwnProduct ? ownProductContactTooltip : undefined}
              className="inline-flex"
            >
              <button
                onClick={() => handleContactAction('TEXT')}
                disabled={isOwnProduct}
                className={`p-2 rounded-md transition-all duration-200 ease-out ${
                  isOwnProduct
                    ? 'bg-secondary-200 text-text-secondary cursor-not-allowed'
                    : 'bg-primary-100 text-primary-700 hover:bg-primary hover:text-white'
                }`}
              >
                <Icon name="MessageCircle" size={16} />
              </button>
            </HoverTooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BiddingPanel;
